import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import '../../App.css';
import DataTable, { Column } from '../../components/DataTable';
import FilterBar from '../../components/FilterBar';
import { nameOnlyFields, FilterValue, applyFilters } from '../../utils/entityFields';
import { ConfirmModal } from '../../components/ui';
import Button from '../../components/ui/Button';
import FormField from '../../components/ui/FormField';
import SearchableSelect from '../../components/SearchableSelect';
import { SkeletonTable } from '../../components/ui/Skeleton';
import EmptyState from '../../components/ui/EmptyState';
import Modal from '../../components/Modal';
import './RequirementTypesPage.css';
import { translateError } from '../../utils/errorMessages';

interface IRequirementType {
  _id: string;
  name: string;
  code: string;
  description?: string;
  inputType: 'boolean' | 'text' | 'file' | 'date' | 'select';
  options?: string[];
  templateFile?: string;
  templateFileName?: string;
  isSystem: boolean;
  isActive: boolean;
}

interface RequirementTypeFormData {
  name: string;
  code: string;
  description: string;
  inputType: 'boolean' | 'text' | 'file' | 'date' | 'select';
  options: string;
}

const RequirementTypesPage: React.FC = () => {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const { addToast } = useToast();
  const [requirementTypes, setRequirementTypes] = useState<IRequirementType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [filters, setFilters] = useState<FilterValue[]>([]);

  // Modal state for create/edit
  const [modalOpen, setModalOpen] = useState(false);
  const [editingType, setEditingType] = useState<IRequirementType | null>(null);
  const [formData, setFormData] = useState<RequirementTypeFormData>({
    name: '',
    code: '',
    description: '',
    inputType: 'boolean',
    options: '',
  });
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Template file upload state
  const [templateUploading, setTemplateUploading] = useState<string | null>(null);

  // Permission checks — requirement types use dedicated requirements permissions
  const canCreate = hasPermission('requirements:write');
  const canEdit = hasPermission('requirements:write');
  const canDelete = hasPermission('requirements:delete');

  const fetchRequirementTypes = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await apiClient.get<IRequirementType[]>('/api/requirements/types');
      setRequirementTypes(data);
      setError(null);
    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRequirementTypes();
  }, [fetchRequirementTypes]);

  const handleCreateClick = () => {
    setEditingType(null);
    setFormData({
      name: '',
      code: '',
      description: '',
      inputType: 'boolean',
      options: '',
    });
    setFormError(null);
    setModalOpen(true);
  };

  const handleEditClick = (item: IRequirementType) => {
    if (item.isSystem) {
      addToast(t('requirements.cannotEditSystem'), 'warning');
      return;
    }

    setEditingType(item);
    setFormData({
      name: item.name,
      code: item.code,
      description: item.description || '',
      inputType: item.inputType,
      options: item.options?.join(', ') || '',
    });
    setFormError(null);
    setModalOpen(true);
  };

  const handleDeleteClick = (item: IRequirementType) => {
    if (!canDelete) return;
    if (item.isSystem) {
      addToast(t('requirements.cannotDeleteSystem'), 'warning');
      return;
    }
    setDeleteId(item._id);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteId) return;
    setDeleteLoading(true);
    try {
      await apiClient.delete(`/api/requirements/types/${deleteId}`);
      fetchRequirementTypes();
      addToast(t('requirements.deleteSuccess'), 'success');
    } catch (err: any) {
      addToast(translateError(err, t, 'requirements.deleteError'), 'error');
    } finally {
      setDeleteLoading(false);
      setDeleteId(null);
    }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!formData.name.trim() || !formData.code.trim()) {
      setFormError(t('requirements.nameCodeRequired'));
      return;
    }

    if (formData.inputType === 'select' && !formData.options.trim()) {
      setFormError(t('requirements.optionsRequired'));
      return;
    }

    setFormSubmitting(true);

    try {
      const payload = {
        name: formData.name.trim(),
        code: formData.code.trim().toUpperCase().replace(/[^A-Z0-9_]/g, '_'),
        description: formData.description.trim(),
        inputType: formData.inputType,
        options: formData.inputType === 'select'
          ? formData.options.split(',').map(o => o.trim()).filter(o => o)
          : undefined,
      };

      if (editingType) {
        await apiClient.put(`/api/requirements/types/${editingType._id}`, payload);
        addToast(t('requirements.updateSuccess'), 'success');
      } else {
        await apiClient.post('/api/requirements/types', payload);
        addToast(t('requirements.createSuccess'), 'success');
      }

      setModalOpen(false);
      fetchRequirementTypes();
    } catch (err: any) {
      setFormError(translateError(err, t));
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleTemplateUpload = useCallback(async (typeId: string, file: File) => {
    setTemplateUploading(typeId);
    try {
      const uploadData = new FormData();
      uploadData.append('file', file);

      await apiClient.post(`/api/requirements/types/${typeId}/template`, uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      fetchRequirementTypes();
      addToast(t('requirements.templateUploadSuccess'), 'success');
    } catch (err: any) {
      addToast(translateError(err, t, 'requirements.templateUploadError'), 'error');
    } finally {
      setTemplateUploading(null);
    }
  }, [fetchRequirementTypes, addToast, t]);

  const handleTemplateDelete = useCallback(async (typeId: string) => {
    setTemplateUploading(typeId);
    try {
      await apiClient.delete(`/api/requirements/types/${typeId}/template`);
      fetchRequirementTypes();
      addToast(t('requirements.templateDeleteSuccess'), 'success');
    } catch (err: any) {
      addToast(translateError(err, t, 'requirements.templateDeleteError'), 'error');
    } finally {
      setTemplateUploading(null);
    }
  }, [fetchRequirementTypes, addToast, t]);

  const getInputTypeLabel = useCallback((inputType: string): string => {
    switch (inputType) {
      case 'boolean':
        return t('requirements.inputTypes.boolean');
      case 'text':
        return t('requirements.inputTypes.text');
      case 'file':
        return t('requirements.inputTypes.file');
      case 'date':
        return t('requirements.inputTypes.date');
      case 'select':
        return t('requirements.inputTypes.select');
      default:
        return inputType;
    }
  }, [t]);

  const columns: Column<IRequirementType>[] = useMemo(() => [
    {
      header: t('common.name'),
      sortValue: (item) => item.name ?? null,
      render: (item) => (
        <div className="name-cell">
          <span className="type-name">{item.name}</span>
          {item.isSystem && (
            <span className="system-badge">{t('requirements.system')}</span>
          )}
        </div>
      ),
    },
    {
      header: t('requirements.code'),
      key: 'code',
      sortValue: (item) => item.code ?? null,
    },
    {
      header: t('requirements.inputType'),
      sortValue: (item) => getInputTypeLabel(item.inputType),
      render: (item) => (
        <span className="input-type-badge">{getInputTypeLabel(item.inputType)}</span>
      ),
    },
    {
      header: t('requirements.templateFile'),
      render: (item) => (
        <div className="template-cell">
          {templateUploading === item._id ? (
            <span className="template-loading">{t('common.loading')}</span>
          ) : item.templateFile ? (
            <div className="template-actions">
              <a
                href={item.templateFile}
                target="_blank"
                rel="noopener noreferrer"
                className="template-link"
                title={item.templateFileName}
              >
                {item.templateFileName || t('requirements.downloadTemplate')}
              </a>
              {canEdit && (
                <button
                  className="template-delete-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleTemplateDelete(item._id);
                  }}
                  title={t('requirements.deleteTemplate')}
                >
                  ×
                </button>
              )}
            </div>
          ) : canEdit ? (
            <label className="template-upload-label">
              <input
                type="file"
                accept=".pdf,.doc,.docx,.odt,.png,.jpg,.jpeg"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    handleTemplateUpload(item._id, file);
                    e.target.value = '';
                  }
                }}
                style={{ display: 'none' }}
              />
              <span className="template-upload-btn">{t('requirements.uploadTemplate')}</span>
            </label>
          ) : (
            <span className="no-template">-</span>
          )}
        </div>
      ),
    },
    {
      header: t('common.description'),
      sortValue: (item) => item.description ?? null,
      render: (item) => (
        <span className="description-cell">{item.description || '-'}</span>
      ),
    },
  ], [t, getInputTypeLabel, templateUploading, canEdit, handleTemplateUpload, handleTemplateDelete]);

  const filteredRequirementTypes = useMemo(
    () => applyFilters(requirementTypes, filters),
    [requirementTypes, filters],
  );

  const handleFilterChange = useCallback((newFilters: FilterValue[]) => {
    setFilters(newFilters);
  }, []);

  if (error) return <p className="error-message">{t('common.error')}: {error}</p>;

  return (
    <div className="admin-list-page requirement-types-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h2>{t('requirements.typesTitle')}</h2>
        {canCreate && (
          <Button onClick={handleCreateClick}>
            {t('requirements.createType')}
          </Button>
        )}
      </div>

      <p className="page-description">{t('requirements.typesDescription')}</p>

      {loading ? (
        <SkeletonTable rows={5} columns={4} />
      ) : requirementTypes.length === 0 ? (
        <EmptyState
          title={t('requirements.emptyTitle')}
          description={t('requirements.emptyDescription')}
          action={canCreate ? { label: t('requirements.createType'), onClick: handleCreateClick } : undefined}
        />
      ) : (
        <>
          <FilterBar fields={nameOnlyFields} onFilterChange={handleFilterChange} />
          <DataTable
            data={filteredRequirementTypes}
            columns={columns}
            onEdit={canEdit ? handleEditClick : undefined}
            onDelete={canDelete ? handleDeleteClick : undefined}
          />
        </>
      )}

      {/* Create/Edit Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingType ? t('requirements.editType') : t('requirements.createType')}
        size="medium"
      >
        <form onSubmit={handleFormSubmit}>
          {formError && <div className="form-error">{formError}</div>}

          <FormField htmlFor="req-name" label={t('common.name')} required>
            <input
              type="text"
              id="req-name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder={t('requirements.namePlaceholder')}
              required
            />
          </FormField>

          <FormField
            htmlFor="req-code"
            label={t('requirements.code')}
            required
            helperText={t('requirements.codeHint')}
          >
            <input
              type="text"
              id="req-code"
              value={formData.code}
              onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value }))}
              placeholder={t('requirements.codePlaceholder')}
              required
              disabled={!!editingType}
            />
          </FormField>

          <FormField htmlFor="req-description" label={t('common.description')}>
            <textarea
              id="req-description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder={t('requirements.descriptionPlaceholder')}
              rows={3}
            />
          </FormField>

          <FormField htmlFor="req-inputType" label={t('requirements.inputType')} required>
            <SearchableSelect
              id="req-inputType"
              value={formData.inputType}
              allowClear={false}
              disabled={!!editingType}
              options={[
                { _id: 'boolean', name: t('requirements.inputTypes.boolean') },
                { _id: 'text', name: t('requirements.inputTypes.text') },
                { _id: 'file', name: t('requirements.inputTypes.file') },
                { _id: 'date', name: t('requirements.inputTypes.date') },
                { _id: 'select', name: t('requirements.inputTypes.select') },
              ]}
              onChange={(value) => {
                if (value != null) {
                  setFormData(prev => ({
                    ...prev,
                    inputType: value as RequirementTypeFormData['inputType'],
                  }));
                }
              }}
            />
          </FormField>

          {formData.inputType === 'select' && (
            <FormField
              htmlFor="req-options"
              label={t('requirements.options')}
              required
              helperText={t('requirements.optionsHint')}
            >
              <input
                type="text"
                id="req-options"
                value={formData.options}
                onChange={(e) => setFormData(prev => ({ ...prev, options: e.target.value }))}
                placeholder={t('requirements.optionsPlaceholder')}
              />
            </FormField>
          )}

          {/* Template file upload - only show when editing */}
          {editingType && (
            <FormField
              htmlFor="req-template"
              label={t('requirements.templateFile')}
              helperText={t('requirements.templateHint')}
              className="template-upload-group"
            >
              <div className="template-upload-container">
                {templateUploading === editingType._id ? (
                  <span className="template-loading">{t('common.loading')}</span>
                ) : editingType.templateFile ? (
                  <div className="template-current">
                    <a
                      href={editingType.templateFile}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="template-link"
                    >
                      {editingType.templateFileName || t('requirements.downloadTemplate')}
                    </a>
                    <button
                      type="button"
                      className="template-remove-btn"
                      onClick={() => handleTemplateDelete(editingType._id)}
                    >
                      {t('common.remove')}
                    </button>
                  </div>
                ) : (
                  <label className="template-upload-label">
                    <input
                      id="req-template"
                      type="file"
                      accept=".pdf,.doc,.docx,.odt,.png,.jpg,.jpeg"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          handleTemplateUpload(editingType._id, file);
                          e.target.value = '';
                        }
                      }}
                      style={{ display: 'none' }}
                    />
                    <span className="template-upload-btn">{t('requirements.uploadTemplate')}</span>
                  </label>
                )}
              </div>
            </FormField>
          )}

          <div className="form-actions">
            <Button type="submit" fullWidth loading={formSubmitting}>
              {formSubmitting
                ? t('common.saving')
                : editingType
                  ? t('common.save')
                  : t('common.create')}
            </Button>
            <Button
              variant="secondary"
              fullWidth
              onClick={() => setModalOpen(false)}
            >
              {t('common.cancel')}
            </Button>
          </div>
        </form>
      </Modal>

      <ConfirmModal
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDeleteConfirm}
        title={t('requirements.deleteTitle')}
        message={t('requirements.confirmDelete')}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        variant="danger"
        loading={deleteLoading}
      />
    </div>
  );
};

export default RequirementTypesPage;
